// src/pages/DebtStudio/components/Portfolios/Analysis/CallOptionality.jsx
import React, { useCallback, useMemo } from "react";
import SectionCard from "components/SectionCard";
import dayjs from "lib/dayjs";

import { buildPortfolioDetails } from "pages/DebtStudio/lib/buildPortfolioDetails";

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import ChartTooltip from "components/ChartTooltip";
import { makeNiceScaleZeroBased } from "lib/chartScale";

/* ------------------------------------------------------------------
 * Chart helpers
 * ------------------------------------------------------------------ */

const CHART_VARS_CALL = {
  callable: "var(--chart-3)",
  noncallable: "var(--chart-4)",
};

function formatKMB(v) {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  if (a >= 1e9)
    return `${(n / 1e9).toLocaleString(undefined, { maximumFractionDigits: 1 })}B`;
  if (a >= 1e6)
    return `${(n / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 })}M`;
  if (a >= 1e3)
    return `${(n / 1e3).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
  return n.toLocaleString();
}

function formatFull2dp(v, s = "$") {
  return `${s}${(Number(v) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function periodLabel(dateStr) {
  const d = dayjs(dateStr);
  if (!d.isValid()) return dateStr;
  return d.format("YYYY-MM-DD");
}

export default function CallOptionality({
  members = [],
  fiscalYearEndOverride = null,
  title = "Callable/Non-Callable Principal by Year",
}) {
  const { callableVsNoncallableOutstandingFY } = useMemo(() => {
    return buildPortfolioDetails({
      members,
      fiscalYearEndOverride,
      // asOfDate: leave null in production
    });
  }, [members, fiscalYearEndOverride]);

  const rows = Array.isArray(callableVsNoncallableOutstandingFY)
    ? callableVsNoncallableOutstandingFY
    : [];

  const hasData = rows.length > 0;

  const maxTotal = useMemo(() => {
    let m = 0;
    for (const r of rows) m = Math.max(m, Number(r.total) || 0);
    return m;
  }, [rows]);

  const scale = useMemo(() => makeNiceScaleZeroBased(maxTotal, 6, 1), [maxTotal]);

  const yTickFormatter = useCallback((v) => `$${formatKMB(v)}`, []);

  return (
    <SectionCard title={title}>
      {!hasData ? (
        <div className="p-4 text-muted">
          No call-optionality data available for the selected issues.
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={rows.map((r) => ({
                  ...r,
                  period: periodLabel(r.date),
                }))}
                margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis
                  domain={[scale.niceMin, scale.niceMax]}
                  ticks={scale.ticks}
                  tickFormatter={yTickFormatter}
                />

                {/* Hide zero-valued categories entirely */}
                <Tooltip
                  content={(props) => {
                    const filteredPayload = (props.payload || []).filter((p) => {
                      const v = Number(p?.value) || 0;
                      return Math.abs(v) > 0.005;
                    });

                    if (!filteredPayload.length) return null;

                    return (
                      <ChartTooltip
                        {...props}
                        payload={filteredPayload}
                        formatter={(val, name) => [formatFull2dp(val, "$"), name]}
                      />
                    );
                  }}
                />

                <Bar
                  dataKey="noncallable"
                  stackId="a"
                  name="Non-callable"
                  fill={CHART_VARS_CALL.noncallable}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey="callable"
                  stackId="a"
                  name="Callable"
                  fill={CHART_VARS_CALL.callable}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Shared legend for this chart */}
          <div className="mt-3 flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-4 border border-border"
                style={{ background: CHART_VARS_CALL.callable }}
              />
              <span className="text-app">Callable</span>
            </div>

            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-4 border border-border"
                style={{ background: CHART_VARS_CALL.noncallable }}
              />
              <span className="text-app">Non-callable</span>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
